'use client'

import {CalendarPeriodPicker, InlineCalendarPeriodPicker} from '../../ui/CalendarPeriodPicker'
import {dateKey} from '../calendar/date'
import {useTranslation} from '../../../hooks/useTranslation'
import {addCalendarDays, isoWeekdayForLocalDate, parseLocalDate} from '@fitro/date-time'
import {calendarDateTimeForLocale, type CalendarDateTimeFormatter} from '@fitro/date-time/display'

// A picker carrier represents an explicit LocalDate, without deriving a day from the device.
function calendarPickerDate(value: string): Date {
    const parts = parseLocalDate(value)
    if (!parts) throw new RangeError('Invalid public calendar date')
    const date = new Date(0)
    date.setFullYear(parts.year, parts.month - 1, parts.day)
    date.setHours(12, 0, 0, 0)
    return date
}

function mondayDateKey(value: string): string {
    return addCalendarDays(value, -(isoWeekdayForLocalDate(value) - 1))
}

export type PublicScheduleView = 'day' | 'week'

export interface PublicScheduleWeekControlsProps {
    compactOnMobile?: boolean
    alwaysShowViewSwitcher?: boolean
    hideViewSwitcher?: boolean
    /** Open the period picker under its trigger on every screen, never as a bottom sheet. */
    inlinePicker?: boolean
    canGoNext?: boolean
    canGoPrevious: boolean
    className?: string
    compactLabel: string
    readableLabel: string
    selectedDateKey: string | null
    selectedWeekStart: string
    todayKey: string
    view: PublicScheduleView
    maxWeekStart?: string
    onNext: () => void
    onPrevious: () => void
    onToday?: () => void
    onSelectDate: (dateKey: string) => void
    onSelectWeek: (weekStart: string) => void
    onViewChange: (view: PublicScheduleView) => void
}

export interface PublicScheduleWeekControlsLabels {
    close: string
    day: string
    nextMonth: string
    nextWeek: string
    previousMonth: string
    previousWeek: string
    selectPeriod: string
    today?: string
    view: string
    week: string
}

export function PublicScheduleWeekControls(props: PublicScheduleWeekControlsProps) {
    const {t, locale} = useTranslation('landing')
    const {t: tCalendar} = useTranslation('client')
    const {t: tCommon} = useTranslation('common')
    return <PublicScheduleWeekControlsView
        {...props}
        dateTime={calendarDateTimeForLocale(locale)}
        labels={{
            close: tCommon('close'),
            day: tCalendar('calendar.views.day'),
            nextMonth: tCalendar('calendar.next'),
            nextWeek: t('publicEntry.schedule.nextWeek'),
            previousMonth: tCalendar('calendar.previous'),
            previousWeek: t('publicEntry.schedule.previousWeek'),
            selectPeriod: tCalendar('calendar.selectPeriod'),
            today: tCalendar('calendar.today'),
            view: tCalendar('calendar.views.label'),
            week: tCalendar('calendar.views.week'),
        }}
    />
}

export function PublicScheduleWeekControlsView({
    compactOnMobile = false,
    alwaysShowViewSwitcher = false,
    hideViewSwitcher = false,
    inlinePicker = false,
    canGoNext = true,
    canGoPrevious,
    className,
    compactLabel,
    readableLabel,
    selectedDateKey,
    selectedWeekStart,
    todayKey,
    view,
    maxWeekStart,
    onNext,
    onPrevious,
    onToday,
    onSelectDate,
    onSelectWeek,
    onViewChange,
    dateTime,
    labels,
}: PublicScheduleWeekControlsProps & {dateTime: CalendarDateTimeFormatter; labels: PublicScheduleWeekControlsLabels}) {
    const dayTitle = view === 'day' && selectedDateKey
    const today = calendarPickerDate(todayKey)
    const minWeekKey = mondayDateKey(todayKey)

    const selectDate = (date: Date) => {
        const nextDateKey = dateKey(date)
        const selectedWeekKey = mondayDateKey(nextDateKey)
        const nextWeekKey = selectedWeekKey < minWeekKey ? minWeekKey : selectedWeekKey
        onSelectDate(view === 'week' ? nextWeekKey : nextDateKey)
        onSelectWeek(nextWeekKey)
    }

    const Picker = inlinePicker ? InlineCalendarPeriodPicker : CalendarPeriodPicker

    return (
        <Picker
            appearance="landing"
            data-testid="public-schedule-toolbar"
            className={className}
            view={view}
            views={hideViewSwitcher ? [view] : ['day', 'week']}
            viewSwitcherCompactOnly={!alwaysShowViewSwitcher}
            onViewChange={nextView => onViewChange(nextView as PublicScheduleView)}
            anchor={calendarPickerDate(selectedDateKey ?? selectedWeekStart)}
            onAnchorChange={selectDate}
            title={dayTitle ? dateTime.formatLocalDate(selectedDateKey, {style: 'dayMonth'}) : compactLabel}
            accessibleTitle={dayTitle
                ? dateTime.formatLocalDateWithWeekday(selectedDateKey, {
                    dateStyle: 'long',
                    weekday: 'long',
                    context: 'sentence',
                    includeYear: true,
                })
                : readableLabel}
            hideViewLabelOnCompact={compactOnMobile}
            previous={{onClick: onPrevious, disabled: !canGoPrevious}}
            next={{onClick: onNext, disabled: !canGoNext}}
            onToday={onToday}
            today={today}
            minWeekStart={calendarPickerDate(minWeekKey)}
            maxWeekStart={maxWeekStart ? calendarPickerDate(maxWeekStart) : undefined}
            dateTime={dateTime}
            labels={{
                selectPeriod: labels.selectPeriod,
                close: labels.close,
                viewSwitcher: labels.view,
                views: {day: labels.day, week: labels.week},
                previousPeriod: labels.previousWeek,
                nextPeriod: labels.nextWeek,
                previousMonth: labels.previousMonth,
                nextMonth: labels.nextMonth,
                today: labels.today,
            }}
        />
    )
}
